/**
 * F35 (Wave 2F) — admin trainer-billing API client.
 *
 * Endpoints:
 *   GET  /admin/trainers/:trainerUserId/billing            → getBilling (active plan + queued switch + rate)
 *   PUT  /admin/trainers/:trainerUserId/billing/override   → applyOverride (plan / rate / trial)
 *   GET  /admin/commission-statements?trainerUserId=&source= → listInvoices (reuses the F23 queue, trainer-scoped)
 *
 * Reads reuse F20's `payments:read`; the override is gated server-side on
 * `trainer_billing:manage` (super_admin + finance). The page-level
 * <ProtectedRoute> gates which routes a given admin role can navigate to; the
 * API client forwards requests and relies on the server to enforce RBAC.
 *
 * Trainer INVOICES (statements) are listed/settled/voided via the EXISTING
 * /admin/commission-statements queue (extended by 2CD with a `source` filter);
 * this client adds a trainer-scoped read helper so the per-trainer billing page
 * can render that trainer's invoices inline without re-shaping the response.
 *
 * Component tests do NOT exercise this real client directly — vitest's config
 * alias rewrites `./api/trainerBilling` → `./api/trainerBilling.fixture`.
 */
import type {
  AdminTrainerBillingOverrideInput,
  CommissionSource,
  CommissionStatement,
  CommissionStatementStatus,
  TrainerActiveBilling,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

/**
 * The admin trainer-billing snapshot returned by
 * `GET /admin/trainers/:id/billing`. Mirrors the trainer self-service
 * `GET /me/trainer/billing` shape (the same service method backs both).
 */
export type AdminTrainerBilling = TrainerActiveBilling;

/** A trainer invoice row — a `commission_statements` row with a trainer source. */
export type AdminTrainerInvoiceRow = CommissionStatement;

export interface AdminTrainerInvoicesListResponse {
  readonly data: readonly AdminTrainerInvoiceRow[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

export interface ListTrainerInvoicesOpts {
  /** Single status value OR comma-separated list. Omit for all. */
  readonly status?: CommissionStatementStatus | 'all';
  readonly page?: number;
  readonly perPage?: number;
}

/**
 * The two trainer-billing sources (commission + flat). The admin queue's
 * `source` filter accepts these plus `'venue'`; the per-trainer invoices list
 * passes the trainer pair so only this trainer's invoices come back.
 */
export const TRAINER_INVOICE_SOURCES: readonly CommissionSource[] = [
  'trainer_commission',
  'trainer_flat',
];

export const adminTrainerBillingApi = {
  /** GET /admin/trainers/:trainerUserId/billing — active plan + queued switch + effective rate. */
  getBilling: async (trainerUserId: string): Promise<AdminTrainerBilling | null> => {
    try {
      const body = await adminFetch<DataEnvelope<AdminTrainerBilling>>(
        `/admin/trainers/${encodeURIComponent(trainerUserId)}/billing`,
      );
      return body.data;
    } catch (err) {
      if (
        err !== null &&
        typeof err === 'object' &&
        'status' in err &&
        (err as { status: number }).status === 404
      ) {
        return null;
      }
      throw err;
    }
  },

  /**
   * PUT /admin/trainers/:trainerUserId/billing/override — override the plan,
   * rate, and/or trial. Applies immediately, forward-only (D10/D11).
   *
   * Permission: `trainer_billing:manage` (super_admin + finance). 422 on empty
   * override / out-of-bounds rate / no-change rate.
   */
  applyOverride: async (
    trainerUserId: string,
    input: AdminTrainerBillingOverrideInput,
  ): Promise<AdminTrainerBilling> => {
    const body = await adminFetch<DataEnvelope<AdminTrainerBilling>>(
      `/admin/trainers/${encodeURIComponent(trainerUserId)}/billing/override`,
      {
        method: 'PUT',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },

  /**
   * GET /admin/commission-statements?trainerUserId=&source=trainer_commission,trainer_flat
   * — this trainer's invoices (statements). Reuses the F23 admin queue endpoint
   * (extended by 2CD with the `source` + `trainerUserId` filters).
   */
  listInvoices: async (
    trainerUserId: string,
    opts?: ListTrainerInvoicesOpts,
  ): Promise<AdminTrainerInvoicesListResponse> => {
    // The admin commission-statements `source` query is a SINGLE `z.enum`
    // (venue | trainer_commission | trainer_flat) — it does NOT accept a
    // comma-separated list. Sending `source=trainer_commission,trainer_flat`
    // 422'd ("Invalid request") and the page surfaced it as a blocking error.
    // Scoping by `trainerUserId` alone already returns only this trainer's
    // statements (a trainer_user_id is set exclusively on trainer-source rows),
    // so the `source` filter is unnecessary here.
    const params: Record<string, string | number | undefined> = {
      trainerUserId,
      page: opts?.page,
      perPage: opts?.perPage,
    };
    if (opts?.status && opts.status !== 'all') {
      params.status = opts.status;
    }
    return adminFetch<AdminTrainerInvoicesListResponse>(
      `/admin/commission-statements${buildQuery(params)}`,
    );
  },
};
